/* Supplier Price List — Vendor Price Memory UI.
 *
 * View + manage a supplier's catalog/price list (item × price × source).
 *
 * Endpoints:
 *   GET    /api/suppliers/:id/price-list
 *   POST   /api/vendor-prices       (manual entry)
 *   DELETE /api/vendor-prices/:id   (admin)
 *   GET    /api/item-master         (typeahead)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Plus, Trash2, X, Tag, ArrowLeft, Save, History, ShieldCheck,
  Truck, Sparkles,
} from 'lucide-react';


const sourceTone = {
  manual:     'bg-blue-100 text-blue-800 border-blue-300',
  rfq_award:  'bg-emerald-100 text-emerald-800 border-emerald-300',
  grn_posted: 'bg-violet-100 text-violet-800 border-violet-300',
};

const sourceLabel = {
  manual:     'Manual',
  rfq_award:  'RFQ Award',
  grn_posted: 'GRN Posted',
};

const tierTone = {
  preferred: 'bg-emerald-50 text-emerald-700',
  approved:  'bg-amber-50 text-amber-700',
  spot:      'bg-gray-50 text-gray-700',
};


export default function SupplierPriceList() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [pricelist, setPricelist] = useState({ by_item: [], all: [], count_items: 0, total: 0 });
  const [items, setItems] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    item_code: '', unit_price: '', lead_time_days: '',
    tier: '', payment_terms: '', valid_until: '', notes: '',
  });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, pl, ims] = await Promise.all([
        axiosInstance.get(`/suppliers/${id}`),
        axiosInstance.get(`/suppliers/${id}/price-list?latest_only=${!showHistory}`),
        axiosInstance.get('/item-master?limit=500'),
      ]);
      setSupplier(s.data || null);
      setPricelist(pl.data || { by_item: [], all: [] });
      setItems(ims.data?.items || []);
    } catch (_) { toast.error('Failed to load price list'); }
  }, [id, showHistory]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    if (!form.item_code || !form.unit_price) {
      toast.error('Item & unit price are required');
      return;
    }
    setBusy(true);
    try {
      await axiosInstance.post('/vendor-prices', {
        supplier_id:    id,
        item_code:      form.item_code,
        unit_price:     Number(form.unit_price),
        lead_time_days: form.lead_time_days ? Number(form.lead_time_days) : undefined,
        tier:           form.tier || undefined,
        payment_terms:  form.payment_terms || undefined,
        valid_until:    form.valid_until || undefined,
        notes:          form.notes || undefined,
      });
      toast.success('Price recorded');
      setShowCreate(false);
      setForm({ item_code: '', unit_price: '', lead_time_days: '',
                tier: '', payment_terms: '', valid_until: '', notes: '' });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || 'Failed');
    } finally { setBusy(false); }
  };

  const remove = async (priceId) => {
    if (!window.confirm('Delete this price entry?')) return;
    try {
      await axiosInstance.delete(`/vendor-prices/${priceId}`);
      toast.success('Deleted');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || 'Failed');
    }
  };

  const rows = showHistory ? pricelist.all : pricelist.by_item;

  return (
    <div className="p-6 space-y-4" data-testid="supplier-pricelist-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <button onClick={() => navigate('/suppliers')}
                  className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Back to Suppliers
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="w-6 h-6 text-indigo-600" />
            {supplier?.name_english || 'Supplier'}
            <span className="text-base font-normal text-gray-600">— Price List</span>
          </h1>
          <div className="text-xs text-gray-500 mt-1">
            {pricelist.count_items} item{pricelist.count_items === 1 ? '' : 's'} ·
            {' '}{pricelist.total} total entries
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowHistory(!showHistory)}
                  data-testid="pricelist-toggle-history"
                  className={`px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1 border ${
                    showHistory ? 'bg-violet-50 border-violet-300 text-violet-800' :
                    'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}>
            <History className="w-4 h-4" /> {showHistory ? 'Latest only' : 'Show history'}
          </button>
          <button onClick={() => setShowCreate(true)}
                  data-testid="pricelist-new-button"
                  className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add Price
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="pricelist-table">
          <thead className="bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Item Code</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2">UOM</th>
              <th className="text-right px-3 py-2">Unit Price</th>
              <th className="text-right px-3 py-2">Lead Time</th>
              <th className="text-left px-3 py-2">Tier</th>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-left px-3 py-2">As of</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="9" className="px-3 py-8 text-center text-sm text-gray-500">
                No prices recorded yet. Click <strong>Add Price</strong> to start, or wait for the first RFQ award / GRN to auto-feed this list.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50"
                  data-testid={`pricelist-row-${r.item_code}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.item_code}</td>
                <td className="px-3 py-2 text-sm">{r.item_description || '—'}</td>
                <td className="px-3 py-2 text-xs">{r.uom || '—'}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-indigo-700">
                  {Number(r.unit_price).toLocaleString()} {r.currency || 'SAR'}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {r.lead_time_days != null ? `${r.lead_time_days}d` : '—'}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.tier && (
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${tierTone[r.tier] || 'bg-gray-50 text-gray-700'}`}>
                      {r.tier}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${sourceTone[r.source] || sourceTone.manual}`}>
                    {sourceLabel[r.source] || r.source}
                  </span>
                  {r.source_ref_no && (
                    <span className="ml-1 text-[10px] font-mono text-gray-500">{r.source_ref_no}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">{r.created_at?.slice(0, 10)}</td>
                <td className="px-3 py-2 text-right">
                  {r.source === 'manual' && (
                    <button onClick={() => remove(r.id)}
                            data-testid={`pricelist-delete-${r.id}`}
                            className="text-gray-400 hover:text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
             data-testid="pricelist-create-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold inline-flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-600" /> Add Vendor Price
              </h3>
              <button onClick={() => setShowCreate(false)}
                      className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-gray-700 font-semibold">Item</label>
                <select value={form.item_code}
                        onChange={(e) => setForm({ ...form, item_code: e.target.value })}
                        data-testid="pricelist-form-item"
                        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5">
                  <option value="">— select item —</option>
                  {items.map((it) => (
                    <option key={it.id || it.item_code} value={it.item_code}>
                      {it.item_code} · {it.description}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-700 font-semibold">Unit Price *</label>
                <input type="number" step="0.01" min="0"
                        value={form.unit_price}
                        onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                        data-testid="pricelist-form-price"
                        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
              </div>
              <div>
                <label className="text-xs text-gray-700 font-semibold">Lead time (days)</label>
                <input type="number" min="0"
                        value={form.lead_time_days}
                        onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })}
                        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
              </div>
              <div>
                <label className="text-xs text-gray-700 font-semibold">Tier</label>
                <select value={form.tier}
                        onChange={(e) => setForm({ ...form, tier: e.target.value })}
                        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5">
                  <option value="">—</option>
                  <option value="preferred">Preferred</option>
                  <option value="approved">Approved</option>
                  <option value="spot">Spot</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-700 font-semibold">Valid until</label>
                <input type="date"
                        value={form.valid_until}
                        onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-700 font-semibold">Payment terms</label>
                <input value={form.payment_terms}
                        onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                        placeholder="Net 30 / 50% advance / …"
                        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-700 font-semibold">Notes</label>
                <textarea value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                            rows={2} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <button onClick={() => setShowCreate(false)}
                      className="px-3 py-1.5 rounded text-sm border border-gray-300 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={submit} disabled={busy}
                      data-testid="pricelist-form-submit"
                      className="px-4 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
                <Save className="w-4 h-4" /> Save Price
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
